import React from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { BaseCard } from './BaseCard';
import { TasksByStatusCardProps } from './CardProps';
import { ScopePieChart } from 'components/Custom/pieChart';
import { DataLogStatus } from 'store/app/enums';

const { Text } = Typography;

// Status color mapping aligned with returns page colors from status-getter.tsx
// Each status has a unique color to distinguish them in the pie chart
// Colors use the generate.TAX theme palette (#41aaaf teal, #3c7edb blue, #C38A20 orange, etc.)
const getStatusColor = (status: DataLogStatus | string): string => {
  // Handle empty/not started status
  if (!status || status === '' || status === 'NOT_STARTED' || status === DataLogStatus.NOT_STARTED) {
    return 'var(--color-text-muted, #5a6a6a)'; // Gray - matches returns page and theme
  }

  switch (status) {
    case DataLogStatus.SAVED:
      return '#3c7edb'; // Blue - matches returns page (#3c7edb blue)
    case DataLogStatus.AWAITING_APPROVAL:
      return '#C38A20'; // Orange/amber - matches returns page (#FFBC3E amber family)
    case DataLogStatus.AWAITING_CLIENT_APPROVAL:
      return '#E09A35'; // Lighter orange/amber - distinct from Awaiting Approval (based on #FFBC3E)
    case DataLogStatus.INTERNAL_APPROVED:
      return '#41aaaf'; // Teal - brand primary
    case DataLogStatus.APPROVED:
      return '#3D8B8F'; // Medium-dark teal - distinct from Internal Approved (based on #41aaaf)
    case DataLogStatus.CLIENT_APPROVED:
      return '#25A5AA'; // Lighter teal - distinct from other teals (based on #41aaaf)
    case DataLogStatus.SUBMITTED:
      return '#1FAFAF'; // Medium teal - distinct from other teals (based on #41aaaf)
    case DataLogStatus.CANCELLED:
      return '#D13131'; // Red - matches returns page (#b94a48 error red family)
    case DataLogStatus.LOCKED:
      return '#6A7A7A'; // Medium-dark gray - distinct from Not Started (#5a6a6a gray family)
    case DataLogStatus.NOT_SAVED:
      return '#9CA7B0'; // Medium-light gray - matches theme placeholder color (#9CA7B0 from theme)
    case DataLogStatus.EXCEPTIONS_MODIFIED:
      return '#B0BCC3'; // Light gray - distinct from Not Saved (between #9CA7B0 and #B8C3C3)
    default:
      return 'var(--color-text-muted, #5a6a6a)'; // Default gray
  }
};

export const TasksByStatusCard: React.FC<TasksByStatusCardProps> = ({
  dashboardViewType,
  cardId,
  savedTasks,
  awaitingClientTasks,
  reviewerApprovedTasks,
  awaitingApprovalTasks,
  approvedTasks,
  clientApprovedTasks,
  lockedTasks,
  submittedTasks,
  cancelledTasks,
  notSavedTasks,
  exceptionsModifiedTasks,
  notStartedTasks,
  handleStatusClick,
  navigateToSubmissions,
  getReturnsByStatus,
}) => {
  const { t } = useTranslation();
  const data =
    dashboardViewType === 'my'
      ? [
          {
            label: t('main.dataLogStatus.saved'),
            value: savedTasks,
            color: getStatusColor(DataLogStatus.SAVED),
            onClick:
              savedTasks > 0
                ? () => handleStatusClick(DataLogStatus.SAVED)
                : undefined,
            returns: getReturnsByStatus(DataLogStatus.SAVED),
          },
          {
            label: t('main.awaiting-client'),
            value: awaitingClientTasks,
            color: getStatusColor(DataLogStatus.AWAITING_CLIENT_APPROVAL),
            onClick:
              awaitingClientTasks > 0
                ? () =>
                    handleStatusClick(DataLogStatus.AWAITING_CLIENT_APPROVAL)
                : undefined,
            returns: getReturnsByStatus(DataLogStatus.AWAITING_CLIENT_APPROVAL),
          },
          {
            label: t('main.dataLogStatus.reviewer-approved'),
            value: reviewerApprovedTasks,
            color: getStatusColor(DataLogStatus.INTERNAL_APPROVED),
            onClick:
              reviewerApprovedTasks > 0
                ? () => handleStatusClick(DataLogStatus.INTERNAL_APPROVED)
                : undefined,
            returns: getReturnsByStatus(DataLogStatus.INTERNAL_APPROVED),
          },
          {
            label: t('main.notStarted'),
            value: notStartedTasks,
            color: getStatusColor(''),
            onClick:
              notStartedTasks > 0
                ? () => navigateToSubmissions('status', '')
                : undefined,
            returns: getReturnsByStatus(''),
          },
        ]
      : [
          {
            label: t('main.dataLogStatus.saved'),
            value: savedTasks,
            color: getStatusColor(DataLogStatus.SAVED),
            onClick:
              savedTasks > 0
                ? () => handleStatusClick(DataLogStatus.SAVED)
                : undefined,
            returns: getReturnsByStatus(DataLogStatus.SAVED),
          },
          {
            label: t('main.dataLogStatus.not-saved'),
            value: notSavedTasks,
            color: getStatusColor(DataLogStatus.NOT_SAVED),
            onClick:
              notSavedTasks > 0
                ? () => handleStatusClick(DataLogStatus.NOT_SAVED)
                : undefined,
            returns: getReturnsByStatus(DataLogStatus.NOT_SAVED),
          },
          {
            label: t('main.dataLogStatus.exceptions-modified'),
            value: exceptionsModifiedTasks,
            color: getStatusColor(DataLogStatus.EXCEPTIONS_MODIFIED),
            onClick:
              exceptionsModifiedTasks > 0
                ? () => handleStatusClick(DataLogStatus.EXCEPTIONS_MODIFIED)
                : undefined,
            returns: getReturnsByStatus(DataLogStatus.EXCEPTIONS_MODIFIED),
          },
          {
            label: t('main.dataLogStatus.awaiting-approval'),
            value: awaitingApprovalTasks,
            color: getStatusColor(DataLogStatus.AWAITING_APPROVAL),
            onClick:
              awaitingApprovalTasks > 0
                ? () => handleStatusClick(DataLogStatus.AWAITING_APPROVAL)
                : undefined,
            returns: getReturnsByStatus(DataLogStatus.AWAITING_APPROVAL),
          },
          {
            label: t('main.dataLogStatus.internal-approved'),
            value: reviewerApprovedTasks,
            color: getStatusColor(DataLogStatus.INTERNAL_APPROVED),
            onClick:
              reviewerApprovedTasks > 0
                ? () => handleStatusClick(DataLogStatus.INTERNAL_APPROVED)
                : undefined,
            returns: getReturnsByStatus(DataLogStatus.INTERNAL_APPROVED),
          },
          {
            label: t('main.awaiting-client'),
            value: awaitingClientTasks,
            color: getStatusColor(DataLogStatus.AWAITING_CLIENT_APPROVAL),
            onClick:
              awaitingClientTasks > 0
                ? () =>
                    handleStatusClick(DataLogStatus.AWAITING_CLIENT_APPROVAL)
                : undefined,
            returns: getReturnsByStatus(DataLogStatus.AWAITING_CLIENT_APPROVAL),
          },
          {
            label: t('main.dataLogStatus.client-approved'),
            value: clientApprovedTasks,
            color: getStatusColor(DataLogStatus.CLIENT_APPROVED),
            onClick:
              clientApprovedTasks > 0
                ? () => handleStatusClick(DataLogStatus.CLIENT_APPROVED)
                : undefined,
            returns: getReturnsByStatus(DataLogStatus.CLIENT_APPROVED),
          },
          {
            label: t('main.dataLogStatus.approved'),
            value: approvedTasks,
            color: getStatusColor(DataLogStatus.APPROVED),
            onClick:
              approvedTasks > 0
                ? () => handleStatusClick(DataLogStatus.APPROVED)
                : undefined,
            returns: getReturnsByStatus(DataLogStatus.APPROVED),
          },
          {
            label: t('main.dataLogStatus.locked'),
            value: lockedTasks,
            color: getStatusColor(DataLogStatus.LOCKED),
            onClick:
              lockedTasks > 0
                ? () => handleStatusClick(DataLogStatus.LOCKED)
                : undefined,
            returns: getReturnsByStatus(DataLogStatus.LOCKED),
          },
          {
            label: t('main.dataLogStatus.cancelled'),
            value: cancelledTasks,
            color: getStatusColor(DataLogStatus.CANCELLED),
            onClick:
              cancelledTasks > 0
                ? () => handleStatusClick(DataLogStatus.CANCELLED)
                : undefined,
            returns: getReturnsByStatus(DataLogStatus.CANCELLED),
          },
          {
            label: t('main.notStarted'),
            value: notStartedTasks,
            color: getStatusColor(''),
            onClick:
              notStartedTasks > 0
                ? () => navigateToSubmissions('status', '')
                : undefined,
            returns: getReturnsByStatus(''),
          },
        ];

  return (
    <BaseCard
      cardId={cardId}
      bodyStyle={{
        paddingTop: '22px',
        paddingBottom: '22px',
        paddingLeft: '24px',
        paddingRight: '24px',
      }}
    >
      <ScopePieChart data={data} size={200} />
    </BaseCard>
  );
};

